import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Truck, CheckCircle2, AlertTriangle, MessageCircle, Pencil, XCircle, RefreshCw, ArrowLeftRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import adminApi from "@/lib/adminApi";
import { ManualTradeInDialog } from "@/components/ManualTradeInTab";
import { buildAuthMessage, buildConfirmMessage, openWhatsApp } from "@/lib/whatsapp";

const money = (n) => (n == null ? "—" : `$${Number(n).toLocaleString("es", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`);
const fmtDate = (iso) => { if (!iso) return "—"; try { const hasTime = typeof iso === "string" && iso.length > 10; const d = new Date(hasTime ? iso : iso + "T00:00:00"); const b = `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`; return hasTime ? `${b} ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}` : b; } catch { return iso; } };
const maskImei = (imei) => (imei ? "•".repeat(Math.max(0, String(imei).length - 4)) + String(imei).slice(-4) : "—");
const WTYPES = [
  ["VENTA_LUIS_PAGADA", "Venta gestionada por el consignador — ya pagada"],
  ["VENTA_LUIS_COBRAR", "Venta gestionada por el consignador — cobrar en PuntoCell"],
  ["ENTREGA_SIN_COBRO", "Entrega autorizada sin cobro"],
  ["OTRO", "Otro"],
];
const WTYPE_BADGE = {
  VENTA_LUIS_PAGADA: ["NO COBRAR — YA PAGADO", "bg-emerald-50 text-emerald-700 border-emerald-200"],
  VENTA_LUIS_COBRAR: ["COBRAR ANTES DE ENTREGAR", "bg-amber-50 text-amber-800 border-amber-300"],
  ENTREGA_SIN_COBRO: ["ENTREGAR SIN COBRO", "bg-sky-50 text-sky-700 border-sky-200"],
  OTRO: ["OTRO — VER INSTRUCCIONES", "bg-zinc-100 text-zinc-600 border-zinc-200"],
};
const FILTERS = [["PENDIENTE", "Pendientes"], ["CONFIRMADO", "Completados"], ["CANCELADO", "Cancelados"]];

// Selector de equipo del consignador (solo iPhones DISPONIBLES del mismo dueño)
function OwnerIphoneSelect({ ownerId, value, onChange, excludeId }) {
  const [opts, setOpts] = useState(null);
  useEffect(() => {
    if (!ownerId) { setOpts([]); return; }
    adminApi.get(`/admin/new-iphones?owner_id=${ownerId}&status=DISPONIBLE`)
      .then((r) => setOpts((r.data || []).filter((x) => x.id !== excludeId)))
      .catch(() => setOpts([]));
  }, [ownerId, excludeId]);
  if (opts === null) return <div className="h-11 rounded-xl border border-zinc-200 flex items-center px-3 text-sm text-zinc-400"><Loader2 className="w-4 h-4 animate-spin mr-2" /> Cargando equipos…</div>;
  return (
    <select data-testid="wd-equipment-select" value={value || ""} onChange={(e) => onChange(e.target.value)} className="h-11 w-full rounded-xl border border-zinc-200 px-3 text-sm bg-white">
      <option value="">— Selecciona el equipo correcto —</option>
      {opts.map((it) => (
        <option key={it.id} value={it.id}>{it.model} {it.capacity}{it.color ? ` · ${it.color}` : ""} · IMEI {maskImei(it.imei)} ({it.id})</option>
      ))}
    </select>
  );
}

export function WithdrawalsTab({ canConfirm }) {
  const [status, setStatus] = useState("PENDIENTE");
  const [rows, setRows] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [confirmed, setConfirmed] = useState(null);
  const [saving, setSaving] = useState(false);
  const [edit, setEdit] = useState(null);        // retiro PENDIENTE a editar
  const [cancelW, setCancelW] = useState(null);   // retiro a cancelar
  const [correct, setCorrect] = useState(null);   // retiro CONFIRMADO a corregir
  const [tradeIn, setTradeIn] = useState(null);    // retiro PENDIENTE para iniciar Trade-In
  const load = (s = status) => { setRows(null); adminApi.get(`/admin/withdrawals?status=${s}`).then((r) => setRows(r.data)).catch(() => setRows([])); };
  useEffect(() => { load(status); /* eslint-disable-next-line */ }, [status]);

  const doConfirm = async () => {
    setSaving(true);
    try { const r = await adminApi.post(`/admin/withdrawals/${confirm.id}/confirm`); toast.success("Entrega confirmada · salida registrada"); setConfirm(null); setConfirmed(r.data); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Error"); } finally { setSaving(false); }
  };

  return (
    <div data-testid="withdrawals-panel">
      <div className="flex flex-wrap gap-2 mb-5">
        {FILTERS.map(([k, label]) => (
          <button key={k} data-testid={`wd-filter-${k}`} onClick={() => setStatus(k)}
            className={`h-9 px-4 rounded-full text-sm font-medium transition-colors ${status === k ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>{label}</button>
        ))}
      </div>
      {rows === null ? <div className="flex justify-center py-16"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div> : rows.length === 0 ? (
        <p className="text-sm text-slate-400 py-10 text-center" data-testid="wd-empty">Sin retiros en esta categoría.</p>
      ) : (
        <div className="space-y-3">
          {rows.map((w) => {
            const [blabel, bstyle] = WTYPE_BADGE[w.withdrawal_type] || ["", ""];
            const badgeText = w.withdrawal_type === "VENTA_LUIS_COBRAR" ? `COBRAR ${money(w.amount_to_collect)} ANTES DE ENTREGAR` : blabel;
            return (
              <div key={w.id} data-testid={`wd-row-${w.id}`} className="rounded-2xl border border-slate-200 bg-white p-4">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0" data-testid={`wd-owner-${w.id}`}>
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-indigo-500">Retiro autorizado por</p>
                    <p className="text-lg font-bold text-slate-900 leading-tight">{w.owner_name || "—"}{w.owner_code ? <span className="text-slate-400 font-semibold"> ({w.owner_code})</span> : null}</p>
                  </div>
                  <span className={`shrink-0 rounded-full border px-2.5 py-1 text-[11px] font-bold ${bstyle}`}>{badgeText}</span>
                </div>
                <div className="mt-3 pt-3 border-t border-slate-100 text-xs text-slate-600 space-y-0.5">
                  <p className="text-sm font-medium text-slate-800">{w.item_label}{w.item_type === "accessory" ? ` ×${w.qty}` : ""}</p>
                  {w.item_type === "iphone" && <p>IMEI: {maskImei(w.imei)}</p>}
                  {w.item_type === "accessory" && w.item_detail && <p>SKU: {w.item_detail}</p>}
                  <p>Retira: <span className="font-medium text-slate-800">{w.pickup_name}</span>{w.pickup_phone ? ` · Tel ${w.pickup_phone}` : ""}{w.pickup_document ? ` · Doc ${w.pickup_document}` : ""}</p>
                  {w.notes && <p className="text-slate-500">Instrucciones: {w.notes}</p>}
                  <p className="text-slate-400">Autorización {w.id} · {fmtDate(w.created_at)}</p>
                  {w.status === "CONFIRMADO" && <p className="text-emerald-700 flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> Entregado por {w.confirmed_by} · {fmtDate(w.confirmed_at)}</p>}
                  {w.status === "CONFIRMADO" && w.corrected_at && <p className="text-indigo-600">Equipo corregido por {w.corrected_by} · {fmtDate(w.corrected_at)}</p>}
                  {w.status === "CONFIRMADO" && w.processed_via === "trade_in" && <p className="text-emerald-700">Procesado por Trade-In · {w.tradein_quote_id}</p>}
                  {w.status === "CANCELADO" && <p className="text-rose-600">Cancelado por {w.cancelled_by} · {fmtDate(w.cancelled_at)}{w.cancel_reason ? ` · ${w.cancel_reason}` : ""}</p>}
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  {w.status === "PENDIENTE" && canConfirm && (
                    <Button data-testid={`wd-confirm-${w.id}`} onClick={() => setConfirm(w)} className="h-9 rounded-lg text-xs bg-slate-900 hover:bg-slate-800"><Truck className="w-3.5 h-3.5 mr-1" /> Confirmar entrega</Button>
                  )}
                  {w.status === "PENDIENTE" && canConfirm && (
                    <Button variant="outline" data-testid={`wd-edit-${w.id}`} onClick={() => setEdit(w)} className="h-9 rounded-lg text-xs"><Pencil className="w-3.5 h-3.5 mr-1" /> Editar</Button>
                  )}
                  {w.status === "PENDIENTE" && canConfirm && (
                    <Button variant="outline" data-testid={`wd-cancel-${w.id}`} onClick={() => setCancelW(w)} className="h-9 rounded-lg text-xs text-rose-600 border-rose-200 hover:bg-rose-50"><XCircle className="w-3.5 h-3.5 mr-1" /> Cancelar</Button>
                  )}
                  {w.status === "PENDIENTE" && w.item_type === "iphone" && canConfirm && (
                    <Button variant="outline" data-testid={`wd-tradein-${w.id}`} onClick={() => setTradeIn(w)} className="h-9 rounded-lg text-xs text-emerald-700 border-emerald-200 hover:bg-emerald-50"><ArrowLeftRight className="w-3.5 h-3.5 mr-1" /> Iniciar Trade-In</Button>
                  )}
                  {w.status === "PENDIENTE" && (
                    <Button data-testid={`wd-share-auth-${w.id}`} onClick={() => openWhatsApp(buildAuthMessage(w))} className="h-9 rounded-lg text-xs bg-[#25D366] hover:bg-[#1ebe5b] text-white"><MessageCircle className="w-3.5 h-3.5 mr-1" /> Compartir autorización</Button>
                  )}
                  {w.status === "CONFIRMADO" && w.item_type === "iphone" && canConfirm && (
                    <Button variant="outline" data-testid={`wd-correct-${w.id}`} onClick={() => setCorrect(w)} className="h-9 rounded-lg text-xs text-indigo-700 border-indigo-200 hover:bg-indigo-50"><RefreshCw className="w-3.5 h-3.5 mr-1" /> Corregir equipo</Button>
                  )}
                  {w.status === "CONFIRMADO" && (
                    <Button data-testid={`wd-share-confirm-${w.id}`} onClick={() => openWhatsApp(buildConfirmMessage(w))} className="h-9 rounded-lg text-xs bg-[#25D366] hover:bg-[#1ebe5b] text-white"><MessageCircle className="w-3.5 h-3.5 mr-1" /> Compartir confirmación</Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Diálogo confirmar entrega */}
      <Dialog open={!!confirm} onOpenChange={(o) => { if (!o) setConfirm(null); }}>
        <DialogContent className="max-w-sm" data-testid="wd-confirm-dialog">
          <DialogHeader><DialogTitle>Confirmar entrega física</DialogTitle></DialogHeader>
          {confirm && (
            <div className="space-y-2 text-sm">
              <p className="text-[11px] font-semibold uppercase text-indigo-500">Autorizado por</p>
              <p className="text-base font-bold text-slate-900 -mt-1">{confirm.owner_name} ({confirm.owner_code})</p>
              <p className="font-medium text-slate-900">{confirm.item_label}{confirm.item_type === "accessory" ? ` ×${confirm.qty}` : ""}</p>
              <p className="text-slate-600">Retira: {confirm.pickup_name}</p>
              <div className="rounded-xl bg-amber-50 border border-amber-200 p-3 text-amber-800 flex gap-2">
                <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                <span>{confirm.withdrawal_type === "VENTA_LUIS_COBRAR" ? `Cobra ${money(confirm.amount_to_collect)} antes de entregar.` : confirm.withdrawal_type === "VENTA_LUIS_PAGADA" ? "NO cobrar — ya pagado al consignador." : confirm.withdrawal_type === "ENTREGA_SIN_COBRO" ? "Entregar sin cobro." : "Revisa las instrucciones del consignador."}</span>
              </div>
              <p className="text-xs text-slate-400">Registra la salida como RETIRO POR DUEÑO. No se puede deshacer.</p>
            </div>
          )}
          <Button data-testid="wd-confirm-submit" onClick={doConfirm} disabled={saving} className="h-11 rounded-xl bg-slate-900 hover:bg-slate-800">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Confirmar entrega"}
          </Button>
        </DialogContent>
      </Dialog>

      {/* Diálogo post-confirmación: compartir por WhatsApp */}
      <Dialog open={!!confirmed} onOpenChange={(o) => { if (!o) setConfirmed(null); }}>
        <DialogContent className="max-w-sm" data-testid="wd-confirmed-dialog">
          <DialogHeader><DialogTitle>Entrega confirmada</DialogTitle></DialogHeader>
          {confirmed && (
            <div className="space-y-3">
              <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-3 text-emerald-800 text-sm flex items-center gap-2"><CheckCircle2 className="w-4 h-4" /> {confirmed.item_label} entregado a {confirmed.pickup_name}.</div>
              <Button data-testid="wd-confirmed-share" onClick={() => openWhatsApp(buildConfirmMessage(confirmed))} className="h-11 w-full rounded-xl bg-[#25D366] hover:bg-[#1ebe5b] text-white"><MessageCircle className="w-4 h-4 mr-2" /> Compartir confirmación por WhatsApp</Button>
              <Button variant="outline" data-testid="wd-confirmed-close" onClick={() => setConfirmed(null)} className="h-11 w-full rounded-xl">Listo</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Editar solicitud PENDIENTE */}
      <EditWithdrawalDialog w={edit} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />

      {/* Cancelar solicitud (con motivo) */}
      <CancelWithdrawalDialog w={cancelW} onClose={() => setCancelW(null)} onDone={() => { setCancelW(null); load(); }} />

      {/* Corregir equipo de un retiro CONFIRMADO */}
      <CorrectEquipmentDialog w={correct} onClose={() => setCorrect(null)} onDone={() => { setCorrect(null); load(); }} />

      {/* Iniciar Trade-In desde una solicitud de retiro pendiente */}
      {tradeIn && (
        <ManualTradeInDialog
          open={!!tradeIn}
          onOpenChange={(o) => { if (!o) setTradeIn(null); }}
          onCreated={() => load()}
          prefill={{
            withdrawalId: tradeIn.id,
            withdrawalNotes: tradeIn.notes,
            customer: { name: tradeIn.pickup_name, phone: tradeIn.pickup_phone },
            outbound: { kind: "new", id: tradeIn.item_id },
          }}
        />
      )}
    </div>
  );
}

function EditWithdrawalDialog({ w, onClose, onSaved }) {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [changeEquip, setChangeEquip] = useState(false);
  useEffect(() => {
    if (w) { setForm({ withdrawal_type: w.withdrawal_type, pickup_name: w.pickup_name || "", pickup_phone: w.pickup_phone || "", pickup_document: w.pickup_document || "", amount_to_collect: w.amount_to_collect != null ? String(w.amount_to_collect) : "", notes: w.notes || "", item_id: "" }); setChangeEquip(false); }
  }, [w]);
  if (!w || !form) return null;
  const needAmount = form.withdrawal_type === "VENTA_LUIS_COBRAR";
  const needNotes = form.withdrawal_type === "OTRO";
  const valid = form.pickup_name.trim() && (!needAmount || Number(form.amount_to_collect) > 0) && (!needNotes || form.notes.trim()) && (!changeEquip || form.item_id);
  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        withdrawal_type: form.withdrawal_type, pickup_name: form.pickup_name.trim(),
        pickup_phone: form.pickup_phone || null, pickup_document: form.pickup_document || null,
        amount_to_collect: needAmount ? Number(form.amount_to_collect) : null,
        notes: form.notes || null,
      };
      if (changeEquip && form.item_id) payload.item_id = form.item_id;
      await adminApi.patch(`/admin/withdrawals/${w.id}`, payload);
      toast.success("Solicitud actualizada");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo editar"); } finally { setSaving(false); }
  };
  return (
    <Dialog open={!!w} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-sm" data-testid="wd-edit-dialog">
        <DialogHeader><DialogTitle>Editar solicitud {w.id}</DialogTitle></DialogHeader>
        <div className="space-y-3 text-sm">
          <div><label className="text-xs text-zinc-500">Persona que retira *</label>
            <Input data-testid="wd-edit-name" value={form.pickup_name} onChange={(e) => setForm({ ...form, pickup_name: e.target.value })} className="h-11 rounded-xl mt-1" placeholder="Nombre completo" /></div>
          <div className="grid grid-cols-2 gap-2">
            <Input data-testid="wd-edit-phone" value={form.pickup_phone} onChange={(e) => setForm({ ...form, pickup_phone: e.target.value })} className="h-11 rounded-xl" placeholder="Teléfono" />
            <Input data-testid="wd-edit-doc" value={form.pickup_document} onChange={(e) => setForm({ ...form, pickup_document: e.target.value })} className="h-11 rounded-xl" placeholder="Documento" />
          </div>
          <div><label className="text-xs text-zinc-500">Modalidad</label>
            <select data-testid="wd-edit-type" value={form.withdrawal_type} onChange={(e) => setForm({ ...form, withdrawal_type: e.target.value })} className="h-11 w-full rounded-xl border border-zinc-200 px-3 text-sm mt-1 bg-white">
              {WTYPES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select></div>
          {needAmount && (
            <div><label className="text-xs text-zinc-500">Monto a cobrar *</label>
              <Input data-testid="wd-edit-amount" type="number" min="0" value={form.amount_to_collect} onChange={(e) => setForm({ ...form, amount_to_collect: e.target.value })} className="h-11 rounded-xl mt-1" /></div>
          )}
          <div><label className="text-xs text-zinc-500">Observaciones{needNotes ? " *" : ""}</label>
            <Input data-testid="wd-edit-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="h-11 rounded-xl mt-1" placeholder="Instrucciones" /></div>
          {w.item_type === "iphone" && (
            <div className="rounded-xl border border-zinc-200 p-3 space-y-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" data-testid="wd-edit-change-equip" checked={changeEquip} onChange={(e) => setChangeEquip(e.target.checked)} className="w-4 h-4 accent-indigo-600" />
                <span className="text-sm font-medium text-zinc-800">Cambiar equipo seleccionado</span>
              </label>
              <p className="text-[11px] text-zinc-400">Actual: {w.item_label} · IMEI {maskImei(w.imei)}</p>
              {changeEquip && <OwnerIphoneSelect ownerId={w.owner_id} value={form.item_id} onChange={(v) => setForm({ ...form, item_id: v })} excludeId={w.item_id} />}
            </div>
          )}
          <Button data-testid="wd-edit-save" onClick={save} disabled={!valid || saving} className="h-11 w-full rounded-xl bg-slate-900 hover:bg-slate-800">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Guardar cambios"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function CancelWithdrawalDialog({ w, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (w) setReason(""); }, [w]);
  if (!w) return null;
  const cancel = async () => {
    if (!reason.trim()) { toast.error("Indica un motivo breve"); return; }
    setSaving(true);
    try { await adminApi.post(`/admin/withdrawals/${w.id}/cancel`, { reason: reason.trim() }); toast.success("Solicitud cancelada"); onDone(); }
    catch (e) { toast.error(e?.response?.data?.detail || "No se pudo cancelar"); } finally { setSaving(false); }
  };
  return (
    <Dialog open={!!w} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-sm" data-testid="wd-cancel-dialog">
        <DialogHeader><DialogTitle>Cancelar solicitud {w.id}</DialogTitle></DialogHeader>
        <div className="space-y-3 text-sm">
          <p className="text-slate-600">{w.item_label} · {w.owner_name}</p>
          <div className="rounded-xl bg-amber-50 border border-amber-200 p-3 text-amber-800 text-xs">La solicitud se marcará como CANCELADA y el equipo volverá a estar disponible. No se registra ninguna venta ni saldo.</div>
          <div><label className="text-xs text-zinc-500">Motivo de cancelación *</label>
            <Input data-testid="wd-cancel-reason" value={reason} onChange={(e) => setReason(e.target.value)} className="h-11 rounded-xl mt-1" placeholder="Ej.: la venta no se concretó" /></div>
          <Button data-testid="wd-cancel-submit" onClick={cancel} disabled={saving || !reason.trim()} className="h-11 w-full rounded-xl bg-rose-600 hover:bg-rose-700">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Cancelar solicitud"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function CorrectEquipmentDialog({ w, onClose, onDone }) {
  const [itemId, setItemId] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (w) setItemId(""); }, [w]);
  if (!w) return null;
  const save = async () => {
    if (!itemId) { toast.error("Selecciona el equipo correcto"); return; }
    setSaving(true);
    try { await adminApi.patch(`/admin/withdrawals/${w.id}`, { item_id: itemId }); toast.success("Equipo corregido · inventario ajustado"); onDone(); }
    catch (e) { toast.error(e?.response?.data?.detail || "No se pudo corregir"); } finally { setSaving(false); }
  };
  return (
    <Dialog open={!!w} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-sm" data-testid="wd-correct-dialog">
        <DialogHeader><DialogTitle>Corregir equipo retirado</DialogTitle></DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="rounded-xl bg-indigo-50 border border-indigo-200 p-3 text-indigo-800 text-xs">El equipo marcado actualmente volverá a <b>DISPONIBLE</b> y el equipo correcto se marcará como <b>RETIRADO</b>, sin duplicar registros.</div>
          <p className="text-[11px] text-zinc-400">Retiro {w.id} · {w.owner_name}</p>
          <p className="text-xs text-zinc-600">Equipo actual (incorrecto): <span className="font-medium">{w.item_label} · IMEI {maskImei(w.imei)}</span></p>
          <div><label className="text-xs text-zinc-500">Equipo realmente retirado *</label>
            <div className="mt-1"><OwnerIphoneSelect ownerId={w.owner_id} value={itemId} onChange={setItemId} excludeId={w.item_id} /></div></div>
          <Button data-testid="wd-correct-submit" onClick={save} disabled={saving || !itemId} className="h-11 w-full rounded-xl bg-indigo-600 hover:bg-indigo-700">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Corregir equipo"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
